import {
	BigNum,
	BN,
	PERP_FEE_TIER_VOLUME_THRESHOLDS,
	QUOTE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import {
	FeeTierDisplayUnit,
	VolumeTier,
} from 'src/components/Modals/FeeTierModal/types';
import { BaseRadioItemProps } from 'src/components/primitives/RadioGroup/types';
import { millifyText } from 'src/utils/formatNumber/millifyText';

export const FEE_TIER_DISPLAY_UNIT_OPTIONS: BaseRadioItemProps<FeeTierDisplayUnit>[] =
	[
		{ id: 'percentage', label: '%', value: 'percentage' },
		{ id: 'bps', label: 'bps', value: 'bps' },
	];

/** The SDK exports the thresholds in quote precision; the modal compares and
 * renders them in whole dollars. */
const toQuoteDollars = (quote: BN) =>
	BigNum.from(quote, QUOTE_PRECISION_EXP).toNum();

/** Abbreviates a threshold for its column heading, e.g. `$5M`. Keeps two
 * decimals so a fractional threshold still labels the value it gates
 * (`$7.5M`), and trims them back off for the round ones. */
const toThresholdLabel = (volume: number) =>
	`$${millifyText(volume, { decimals: 2, trimEndingZeroes: true })}`;

/** Tier names, in ladder order. Presentation only: the program selects by
 * index. A rung the SDK ladder gains before this list does falls back to
 * `VIP <n>` rather than dropping out of the table. */
const TIER_TITLES = ['Regular', 'VIP 1', 'VIP 2', 'VIP 3'];

/**
 * One row per rung of the program's fee ladder, built from the SDK's
 * `PERP_FEE_TIER_VOLUME_THRESHOLDS` so the row count and breakpoints follow
 * `determine_perp_fee_tier` rather than being restated here. There is one more
 * tier than there are breakpoints between them.
 *
 * `amount` is the tier's own 30d volume floor; `display` is the threshold that
 * separates it from the next tier, so the first row reads "< $5M" and the rest
 * read "≥ <their own floor>".
 */
export const VOLUME_TIERS: VolumeTier[] = [
	{
		title: TIER_TITLES[0],
		amount: 0,
		// The entry tier has no floor of its own, so it labels the floor above it.
		display: toThresholdLabel(
			toQuoteDollars(PERP_FEE_TIER_VOLUME_THRESHOLDS[0])
		),
	},
	...PERP_FEE_TIER_VOLUME_THRESHOLDS.map((threshold, index) => {
		const floor = toQuoteDollars(threshold);

		return {
			title: TIER_TITLES[index + 1] ?? `VIP ${index + 1}`,
			amount: floor,
			display: toThresholdLabel(floor),
		};
	}),
];

/**
 * Markets the fee schedule prices separately from everything else. Which
 * markets count as majors is a product decision, not something the program
 * encodes, so it cannot be derived from chain.
 */
export const MAJOR_MARKET_BASE_SYMBOLS: string[] = ['BTC', 'ETH', 'SOL'];

export const MAJOR_MARKETS_LABEL = MAJOR_MARKET_BASE_SYMBOLS.join('/');
export const OTHER_MARKETS_LABEL = 'Other Markets';
